import { useEffect, useRef, useState, type ReactNode } from 'react'
import { FOCUSABLE, trapStep } from '../lib/focusTrap'
import { Icon } from './icons'
import { useStaffText } from './StaffText'

/**
 * A centred dialog for a row's details or a short editor (ui/Dialog's classes).
 * Escape or the scrim closes it; the first field takes focus, Tab stays inside and
 * focus returns on close. Once something has been typed, a stray click outside
 * (or Escape) asks before throwing the edits away; the footer's close button is
 * deliberate and closes straight away.
 */
export function Drawer({
  title,
  mark,
  onClose,
  onSubmit,
  submitLabel,
  valid = true,
  busy = false,
  extra,
  closeLabel,
  hideSubmit = false,
  wide = false,
  tone,
  children,
}: {
  title: string
  /** Drawn before the title: a queue's stub, a desk's number. */
  mark?: ReactNode
  onClose: () => void
  onSubmit?: () => void
  submitLabel?: string
  valid?: boolean
  /** A save in flight: the submit button waits. */
  busy?: boolean
  /** Left side of the footer: a destructive action, or a short note. */
  extra?: ReactNode
  /** The footer's close button; defaults to Cancel. */
  closeLabel?: string
  /** Hide the submit button, e.g. while nothing has changed, or a read-only panel. */
  hideSubmit?: boolean
  /** A wider panel, for a table or a long form. */
  wide?: boolean
  /** Defaults to dark for an editor (it has onSubmit), light for a read-only panel. */
  tone?: 'light' | 'dark'
  children: ReactNode
}) {
  const { say } = useStaffText()
  const form = useRef<HTMLFormElement>(null)
  const dirty = useRef(false)
  const [asking, setAsking] = useState(false)
  const leave = () => (dirty.current ? setAsking(true) : onClose())
  const leaveRef = useRef(leave)
  leaveRef.current = leave

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const field = form.current?.querySelector<HTMLElement>('.dialog__body input, .dialog__body select, .dialog__body textarea')
    ;(field ?? form.current)?.focus()
    const on = (e: KeyboardEvent) => {
      // A select's own list closes on Escape first.
      if (e.key === 'Escape' && !e.defaultPrevented) leaveRef.current()
      if (e.key === 'Tab' && !e.defaultPrevented && form.current) {
        const items = [...form.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null)
        const active = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const to = trapStep(items, active, e.shiftKey)
        if (to) {
          e.preventDefault()
          to.focus()
        }
      }
    }
    addEventListener('keydown', on)
    return () => {
      removeEventListener('keydown', on)
      if (before?.isConnected) before.focus()
    }
  }, [])

  const submit = !hideSubmit && onSubmit && submitLabel
  const dark = (tone ?? (onSubmit ? 'dark' : 'light')) === 'dark'

  return (
    <div className="dialog-scrim" onMouseDown={(e) => e.target === e.currentTarget && leave()}>
      <form
        ref={form}
        className={'dialog' + (dark ? ' dialog--dark' : '') + (wide ? ' dialog--wide' : '')}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={title}
        onSubmit={(e) => {
          e.preventDefault()
          if (valid && !busy && onSubmit) onSubmit()
        }}
        onInput={() => {
          dirty.current = true
        }}
        onChange={() => {
          dirty.current = true
        }}
      >
        <header className="dialog__head">
          {mark}
          <div className="dialog__titles">
            <h2 className="dialog__title">{title}</h2>
          </div>
          <button type="button" className="dialog__close" onClick={onClose} aria-label={say('ui.close')}>
            <Icon name="close" size={16} />
          </button>
        </header>
        <div className="dialog__body">{children}</div>
        {asking ? (
          <footer className="dialog__foot drawer__foot--ask" role="alertdialog" aria-label={say('ui.unsaved')}>
            <div className="drawer__note">
              <strong>{say('ui.discardAsk')}</strong>
            </div>
            <button type="button" className="btn btn--quiet" autoFocus onClick={() => setAsking(false)}>
              {say('ui.keepEditing')}
            </button>
            <button type="button" className="btn btn--danger" onClick={onClose}>
              {say('ui.discard')}
            </button>
          </footer>
        ) : (
          <footer className="dialog__foot">
            <div className="drawer__note">{extra}</div>
            <button type="button" className="btn btn--quiet" onClick={onClose}>
              {closeLabel ?? (submit ? say('common.cancel') : say('ui.close'))}
            </button>
            {submit && (
              <button className="btn btn--primary" disabled={!valid || busy}>
                {submitLabel}
              </button>
            )}
          </footer>
        )}
      </form>
    </div>
  )
}
